import React from "react";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * ReminderPanel — Formlara gömülen "Hatırlatma Kur" paneli.
 *
 * Props:
 *   aktif          : bool   — checkbox durumu
 *   setAktif       : fn     — checkbox değiştirici
 *   saat           : string — "HH:MM" formatı
 *   setSaat        : fn
 *   tarih          : string — "YYYY-MM-DD" (tarih seçici bazı modlarda kullanılır)
 *   setTarih       : fn
 *   tekrar         : string — "none"|"daily"|"weekly"|"monthly"
 *   setTekrar      : fn
 *   mod            : string — "datetime" | "offset" | "time-only"
 *                    datetime : tarih + saat (alışveriş, görev, takvim)
 *                    offset   : "X gün önce saat HH:MM" (finans ödemeleri)
 *                    time-only: sadece saat (tekrarlı alışveriş hatırlatmaları)
 *   onceDays       : number — offset modunda "kaç gün önce"
 *   setOnceDays    : fn
 *   referansTarih  : string — offset modunda baz tarih (ör. son ödeme tarihi)
 *   etiket         : string — checkbox yanındaki metin (varsayılan: "Hatırlatma kur")
 */
export default function ReminderPanel({
  aktif, setAktif,
  saat = "", setSaat,
  tarih = "", setTarih,
  tekrar = "none", setTekrar,
  mod = "datetime",
  onceDays = 1, setOnceDays,
  referansTarih = "",
  etiket = "Hatırlatma kur",
}) {
  const { t, lang } = useLanguage();
  const fieldId = React.useId();
  const labelText = etiket === "Hatırlatma kur" ? t("reminder.set") : etiket;
  const tekrarSecenekleri = [
    { value: "none",    label: t("reminder.once") },
    { value: "daily",   label: t("reminder.daily") },
    { value: "weekly",  label: t("reminder.weekly") },
    { value: "monthly", label: t("reminder.monthly") },
  ];

  const bugunInputValue = React.useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const gun = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${gun}`;
  }, []);

  React.useEffect(() => {
    if (aktif && mod === "datetime" && !tarih && typeof setTarih === "function") {
      setTarih(bugunInputValue);
    }
  }, [aktif, mod, tarih, setTarih, bugunInputValue]);

  // offset modunda hedef tarihi hesapla (kullanıcıya göster)
  const offsetHedefTarih = React.useMemo(() => {
    if (mod !== "offset" || !referansTarih || !onceDays) return null;
    const ref = new Date(referansTarih);
    if (isNaN(ref)) return null;
    ref.setDate(ref.getDate() - Number(onceDays));
    return ref.toLocaleDateString(lang === "en" ? "en-US" : "tr-TR", { day: "numeric", month: "long", year: "numeric" });
  }, [mod, referansTarih, onceDays, lang]);

  return (
    <div className="reminderPanel">
      {/* Checkbox satırı */}
      <label className="reminderCheckLabel">
        <span className="reminderCheckBox">
          <input
            type="checkbox"
            checked={aktif}
            onChange={(e) => setAktif(e.target.checked)}
          />
          <span className="reminderCheckMark">{aktif ? "✓" : ""}</span>
        </span>
        <span className="reminderCheckText">🔔 {labelText}</span>
      </label>

      {/* Ayar paneli */}
      {aktif && (
        <div className="reminderSettings">

          {/* DATETIME modu: tarih + saat */}
          {mod === "datetime" && (
            <div className="formRow">
              <div className="inputLabelGroup">
                <label className="darkLabel" htmlFor={`${fieldId}-date`}>{t("reminder.date")}</label>
                <input
                  id={`${fieldId}-date`}
                  className="darkInput"
                  type="date"
                  value={tarih}
                  min={bugunInputValue}
                  onChange={(e) => setTarih(e.target.value)}
                />
              </div>
              <div className="inputLabelGroup">
                <label className="darkLabel" htmlFor={`${fieldId}-time`}>{t("reminder.time")}</label>
                <input
                  id={`${fieldId}-time`}
                  className="darkInput"
                  type="time"
                  value={saat}
                  onChange={(e) => setSaat(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* OFFSET modu: X gün önce + saat */}
          {mod === "offset" && (
            <>
              <div className="formRow">
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("reminder.daysBefore")}</label>
                  <select
                    className="darkInput"
                    value={onceDays}
                    onChange={(e) => setOnceDays(Number(e.target.value))}
                  >
                    {[0, 1, 2, 3, 5, 7, 10, 14, 30].map((d) => (
                      <option key={d} value={d}>{d === 0 ? t("reminder.sameDay") : `${d} ${t("reminder.daysBeforeSuffix")}`}</option>
                    ))}
                  </select>
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("reminder.time")}</label>
                  <input
                    className="darkInput"
                    type="time"
                    value={saat}
                    onChange={(e) => setSaat(e.target.value)}
                  />
                </div>
              </div>
              {offsetHedefTarih && (
                <div className="reminderOffsetInfo">
                  📅 {t("reminder.willSend")}: <b>{offsetHedefTarih}</b>
                </div>
              )}
              {!referansTarih && (
                <div className="reminderOffsetInfo reminderOffsetWarn">
                  ⚠️ {t("reminder.needReferenceDate")}
                </div>
              )}
            </>
          )}

          {/* TIME-ONLY modu: sadece saat */}
          {mod === "time-only" && (
            <div className="inputLabelGroup">
              <label className="darkLabel">{t("reminder.time")}</label>
              <input
                className="darkInput"
                type="time"
                value={saat}
                onChange={(e) => setSaat(e.target.value)}
              />
            </div>
          )}

          {/* {t("reminder.repeat")} seçeneği — tüm modlarda */}
          <div className="inputLabelGroup">
            <label className="darkLabel">{t("reminder.repeat")}</label>
            <select
              className="darkInput"
              value={tekrar}
              onChange={(e) => setTekrar(e.target.value)}
            >
              {tekrarSecenekleri.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

        </div>
      )}
    </div>
  );
}

export function ReminderWarning({ message }) {
  if (!message) return null;
  return (
    <div
      className="reminderInlineWarning"
      role="alert"
      style={{
        margin: "8px 0 10px",
        padding: "9px 12px",
        borderRadius: 14,
        border: "1px solid rgba(245, 158, 11, 0.42)",
        background: "var(--warning-soft-bg, rgba(245, 158, 11, 0.13))",
        color: "var(--text-color, var(--text-primary, #1f2937))",
        fontSize: 13,
        lineHeight: 1.35,
        boxShadow: "0 8px 20px rgba(0, 0, 0, 0.08)",
      }}
    >
      ⚠️ {message}
    </div>
  );
}

/**
 * Hatırlatma panelinden scheduled notification payload'ı oluşturur.
 * Döner: { planlananTarih: Date, tekrarTipi: string } | null
 */
const tarihStringiniLocalDateYap = (value) => {
  if (!value) return null;
  if (value instanceof Date) return new Date(value.getTime());

  const str = String(value);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export function reminderPayloadOlustur({ mod, tarih, saat, tekrar, onceDays, referansTarih, includeReason = false }) {
  const saatStr = saat || "09:00";
  const [sH, sM] = saatStr.split(":").map(Number);

  if (mod === "datetime" || mod === "time-only") {
    if (!tarih) return includeReason ? { ok: false, reason: "missingDate" } : null;
    const dt = tarihStringiniLocalDateYap(tarih);
    if (!dt) return includeReason ? { ok: false, reason: "invalidDate" } : null;
    dt.setHours(Number.isFinite(sH) ? sH : 9, Number.isFinite(sM) ? sM : 0, 0, 0);
    if (dt.getTime() <= Date.now()) return includeReason ? { ok: false, reason: "pastDate" } : null;
    const payload = { planlananTarih: dt, tekrarTipi: tekrar || "none" };
    return includeReason ? { ok: true, payload } : payload;
  }

  if (mod === "offset") {
    if (!referansTarih) return includeReason ? { ok: false, reason: "missingReferenceDate" } : null;
    const ref = tarihStringiniLocalDateYap(referansTarih);
    if (!ref) return includeReason ? { ok: false, reason: "invalidReferenceDate" } : null;
    ref.setDate(ref.getDate() - Number(onceDays ?? 1));
    ref.setHours(Number.isFinite(sH) ? sH : 9, Number.isFinite(sM) ? sM : 0, 0, 0);
    if (ref.getTime() <= Date.now()) return includeReason ? { ok: false, reason: "pastDate" } : null;
    const payload = { planlananTarih: ref, tekrarTipi: tekrar || "none" };
    return includeReason ? { ok: true, payload } : payload;
  }

  return includeReason ? { ok: false, reason: "invalidMode" } : null;
}

export function reminderPayloadUyarisi(result, t, lang = "tr") {
  if (!result || result.ok !== false) return "";
  const key = result.reason === "missingDate" || result.reason === "missingReferenceDate"
    ? "reminder.invalid.missingDate"
    : result.reason === "pastDate"
      ? "reminder.invalid.pastDate"
      : "reminder.invalid.general";
  const fallback = lang === "en"
    ? "Reminder could not be created. Please choose a valid future date and time."
    : "Hatırlatma oluşturulamadı. Lütfen geçerli bir ileri tarih ve saat seç.";
  return typeof t === "function" ? t(key) : fallback;
}

export function reminderPayloadKontrolEt(params, t, lang = "tr", setError) {
  const result = reminderPayloadOlustur({ ...params, includeReason: true });
  if (result?.ok) {
    if (typeof setError === "function") setError("");
    return result.payload;
  }
  const message = reminderPayloadUyarisi(result, t, lang);
  if (typeof setError === "function") setError(message);
  return null;
}

